import { RegisterOptions } from 'react-hook-form';
import { i18n } from '@lingui/core';
import {t} from '@lingui/core/macro';

interface BaseValidationRule {
    type: string;
    errorMessage: string;
}

interface PatternRule extends BaseValidationRule {
    type: 'pattern';
    regex: string;
    inverse?: boolean;
}

interface LengthCheckRule extends BaseValidationRule {
    type: 'lengthCheck';
    maxLength: number;
}

interface RequiredRule extends BaseValidationRule {
    type: 'required';
}

type ValidationRule = PatternRule | LengthCheckRule | RequiredRule;

const validationRules = (): ValidationRule[] => {
    return [
        {
            type: 'required',
            errorMessage: i18n._('validation.requiredUrl'),
        },
        {
            type: 'pattern',
            regex: '^(https?:\\/\\/)',
            errorMessage: i18n._('validation.invalidProtocol'),
        },
        {
            type: 'pattern',
            regex: '^https?:\\/\\/.+',
            errorMessage: i18n._('validation.missingDomain'),
        },
        {
            type: 'pattern',
            regex: '[<>"\'`]',
            errorMessage: i18n._('validation.invalidCharacters'),
            inverse: true,
        },
        {
            type: 'pattern',
            // eslint-disable-next-line max-len
            regex: '^(?!https?:\\/\\/(?:(?:10(?:\\.[0-9]{1,3}){3})|(?:172\\.(?:1[6-9]|2[0-9]|3[01])(?:\\.[0-9]{1,3}){2})|(?:192\\.168(?:\\.[0-9]{1,3}){2})|(?:100\\.(?:6[4-9]|[7-9][0-9]|1[01][0-9]|12[0-7])(?:\\.[0-9]{1,3}){2}))(?::[0-9]+)?(?:\\/|$)).+',
            errorMessage: i18n._('validation.internalAddress'),
        },
        {
            type: 'pattern',
            regex: '^https?:\\/\\/[.-]|\\/[.-]',
            errorMessage: i18n._('validation.invalidDomainStart'),
            inverse: true,
        },
        {
            type: 'lengthCheck',
            maxLength: 2096,
            errorMessage: i18n._('validation.urlTooLong'),
        },
    ];
};

function isPatternRule(rule: ValidationRule): rule is PatternRule {
    return rule.type === 'pattern';
}

function isLengthCheckRule(rule: ValidationRule): rule is LengthCheckRule {
    return rule.type === 'lengthCheck';
}

function isRequiredRule(rule: ValidationRule): rule is RequiredRule {
    return rule.type === 'required';
}

function getDomain(url: string) {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;
        const parts = hostname.split('.');

        if (parts.length < 2 || parts[parts.length - 1].length < 2) {
            return null;
        }
        if (hostname.includes('%')) {
            return null;
        }
        return hostname;
    } catch {
        return null;
    }
}

const processUrlValue = (value: string): string => {
    return value.trim().replace(/\s+/g, '%20');
};

const getRequiredRule = (): RequiredRule | undefined => {
    return validationRules().find(isRequiredRule);
};

const validateUrlWithRules = (value: string, requiredRule = getRequiredRule()): string | true => {
    if (!value || value.trim() === '') {
        return requiredRule ? requiredRule.errorMessage : true;
    }

    const processedValue = processUrlValue(value);
    const hostname = getDomain(processedValue);

    if (hostname && /[_`!@#$^&*()=+{};'",<>?/\\|~]/.test(hostname)) {
        return t`Адрес содержит недопустимые символы в доменном имени`;
    }

    for (const rule of validationRules()) {
        if (isPatternRule(rule)) {
            const regex = new RegExp(rule.regex);
            const testResult = regex.test(processedValue);

            if (rule.inverse ? testResult : !testResult) {
                return rule.errorMessage;
            }
        }

        if (isLengthCheckRule(rule)) {
            if (processedValue.length > rule.maxLength) {
                return rule.errorMessage;
            }
        }
    }

    return true;
};

export const validateMultipleUrls = (value: string): string | true => {
    const requiredRule = getRequiredRule();

    if (!value || value.trim() === '') {
        return requiredRule ? requiredRule.errorMessage : true;
    }

    const errors: string[] = [];
    const urls = value.split(/\r?\n/);
    const hasNonEmptyUrls = urls.some(url => url.trim() !== '');

    if (!hasNonEmptyUrls && requiredRule) {
        return requiredRule.errorMessage;
    }

    for (const url of urls) {
        if (!url.trim()) continue;

        const validationResult = validateUrlWithRules(url);
        if (validationResult !== true) {
            errors.push(`• ${validationResult}: ${url}`);
        }
    }

    if (errors.length === 0) {
        return true;
    }

    return errors.join('\n');
};

export const createSingleUrlValidator = (): RegisterOptions => {
    const requiredRule = getRequiredRule();
    const rules: RegisterOptions = {
        setValueAs: (value: string) => {
            if (!value || value.trim() === '') return '';
            return processUrlValue(value);
        },
        validate: (value: string) => validateUrlWithRules(value, requiredRule),
    };

    if (requiredRule) {
        rules.required = requiredRule.errorMessage;
    }

    return rules;
};